import type { DateTime } from "luxon";
import { OUTREACH_LEVELS, OutreachLevel, type OutreachScoredPerson, type RequestOutreach } from "../../../utils/models";
import type { MetaChipTone } from "../../UIToolkit/Badges/MetaChip";

export enum OutreachTab {
	Reached = "reached",
	UpNext = "up_next",
	NotTexting = "not_texting",
}

export enum OutreachGroupIcon {
	Message = "message",
	AppAlert = "app_alert",
	Call = "call",
	Overtime = "overtime",
	Agency = "agency",
	Skipped = "skipped",
}

export enum OutreachRowAction {
	/** Text them now, or call them while calling. */
	SendNow = "send_now",
	/** Call them now: a row on the call list, before calls start or while they run. */
	CallNow = "call_now",
	/** Offer the shift now to someone it would put into daily overtime, ahead of the Overtime level. */
	OfferNow = "offer_now",
	Skip = "skip",
	Undo = "undo",
	TextAnyway = "text_anyway",
}

/** `solid` fills the pill: an outcome (accepted, approved) rather than a note about the person. */
export type OutreachRowBadge = { id: string; label: string; tone: MetaChipTone; solid: boolean };

export type OutreachRowView = {
	nurseId: number;
	name: string;
	/** Present for scored people: drives the match meter and the Why chips. */
	scored: OutreachScoredPerson | null;
	/** Why someone unscored is here (a filter's reason); `null` for scored people. */
	line: string | null;
	badges: OutreachRowBadge[];
	/** When we'll call them, on a phone-calls row; `null` everywhere else. */
	when: string | null;
	actions: OutreachRowAction[];
};

/** Every phone-calls group's id starts with this, so its rows get call test ids. */
export const CALLS_GROUP_ID = "calls";

export type OutreachGroupView = {
	id: string;
	title: string;
	meta: string | undefined;
	icon: OutreachGroupIcon;
	/** An explanation under the title; a group can be only this, with no people (the agency post, a level that's off). */
	note: string | undefined;
	rows: OutreachRowView[];
	/** Whether its people count toward the tab's number: an app alert and a call preview reach people counted elsewhere. */
	countsInTab: boolean;
	defaultOpen: boolean;
};

/** Row and label builders shared by the Reached and Up next groups. */
export type OutreachGroupHelpers = {
	people: (count: number) => string;
	/** "Today · 10:00 AM", "Tomorrow · 10:00 AM", or the date and time. */
	dayMeta: (at: DateTime) => string;
	/** The time on a call row: the group already names the day, so a later day only needs its weekday. */
	rowTimeOf: (at: DateTime) => string;
	scoredRow: (person: OutreachScoredPerson, badges: OutreachRowBadge[], actions: OutreachRowAction[], when: string | null) => OutreachRowView;
};

export const levelIndexOf = (level: OutreachLevel): number => OUTREACH_LEVELS.indexOf(level);

/** "Level 3": levels are numbered from 1. */
export const levelNumberOf = (level: OutreachLevel): number => levelIndexOf(level) + 1;

export const isAtLeast = (outreach: RequestOutreach, level: OutreachLevel): boolean => levelIndexOf(outreach.effort.level) >= levelIndexOf(level);

/** Calls replace texts from the Calls level, when calls are allowed. */
export const isCalling = (outreach: RequestOutreach): boolean => outreach.effort.allowed.calls && isAtLeast(outreach, OutreachLevel.Calls);

/** When a level starts, or `null` when it won't (held). */
export const levelStartOf = (outreach: RequestOutreach, level: OutreachLevel): DateTime | null => {
	const entry = outreach.effort.schedule.find((candidate) => candidate.level === level);
	if (entry === undefined) throw new Error(`Outreach: level ${level} is missing from the schedule`);
	return entry.startsAt;
};
